import camelCase from "lodash/camelCase";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { Badge, Box, Flex, H, Icon, Text } from "dash-ui";
import { RecurringTypesEnum } from "dash-common";

import { getFeatureLabels } from "pages/ProfilePage/components/ProfileBilling/components/SubscriptionProductsSection/components/ProductCardItem/ProductCardItem.helpers";

import { ISubscriptionCard } from "./SubscriptionCard.interfaces";
import ActionButtons from "./components/ActionButtons";

const Card = styled(Flex)<{ isLight: boolean; ignoreRightMargin?: boolean }>`
  border-radius: ${({ theme }) => theme.space * 2}px;
  box-shadow: ${({ theme }) => theme.shadows.subscriptionCardShadow};
  background-color: ${({ isLight, theme }) =>
    isLight ? theme.colors.white : theme.colors.b100};
  margin-right: ${({ theme, ignoreRightMargin }) =>
    !ignoreRightMargin ? theme.space * 8 : 0}px;

  @media ${({ theme }) => theme.devices.mobile} {
    margin-right: 0px;
  }
`;
const BadgeStyle = styled(Badge)`
  text-decoration: line-through;
`;

function SubscriptionCard({
  currentBrokerSubscription,
  subscription,
  isLight,
  activeRecurringType,
  ignoreRightMargin,
  showTrial,
  onSubscriptionProductSelect,
  onSubscriptionProductCancel,
  selectButtonLoading,
  cancelButtonLoading,
  isCurrent,
  type,
  onClose,
}: ISubscriptionCard) {
  const { t, i18n } = useTranslation(["pages", "common"]);
  const theme = useTheme();

  const { coupon, price, name, recurringType, trial } = subscription;

  const displayPrice =
    recurringType === RecurringTypesEnum.SIXMONTH
      ? (price / 6).toFixed(2)
      : price;

  const discount =
    coupon?.active && coupon?.percentOff
      ? price * (coupon.percentOff / 100)
      : 0;
  const discountTail =
    activeRecurringType === RecurringTypesEnum.YEAR
      ? `/${t(
          `common:subscriptionProductRecurringLabels:${camelCase(
            activeRecurringType
          )}`
        )}`
      : "";

  const featuresList = getFeatureLabels(subscription, {
    t,
    i18n,
  });

  function getBadgeTitle() {
    if (subscription.price === 0)
      return t("common:subscriptionBadgeTitles.noCreditCard");
    if (trial && showTrial)
      return t("common:subscriptionBadgeTitles.trialAvailable");

    const recurringTitles = {
      [RecurringTypesEnum.MONTH]: t(
        "common:subscriptionBadgeTitles.saveWithSixMonthPlan"
      ),
      [RecurringTypesEnum.SIXMONTH]: t(
        "common:subscriptionBadgeTitles.billedEverySixMonths"
      ),
    };

    return recurringTitles[subscription.recurringType] || "";
  }

  return (
    <Card
      isLight={isLight}
      flexDirection="column"
      justifyContent="space-between"
      flex={1}
      pos="relative"
      minWidth={325}
      maxWidth={370}
      minHeight={500}
      w="100%"
      p={6}
      flexGrow={1}
      ignoreRightMargin={ignoreRightMargin}>
      {subscription.name === "DASH Exchange" && (
        <Text
          bold
          color={theme.colors.b50}
          className="absolute top-0 right-0 rounded-tr-md rounded-bl-3xl bg-white w-20 h-8 flex justify-center items-center">
          NEW
        </Text>
      )}

      <Flex flexDirection="column" w="100%">
        <Flex alignItems="center">
          <Text color={isLight ? theme.colors.b50 : theme.colors.y20} xl>
            {name}
          </Text>
          {isCurrent && (
            <Badge
              borderRadius={theme.space * 2}
              ml={2}
              type="active"
              title={t("common:labels.current")}
            />
          )}
        </Flex>

        {coupon && coupon.active && (
          <Flex>
            <Box mr={1}>
              <Text color={isLight ? theme.colors.r20 : theme.colors.white}>
                {t("common:labels:save")} {coupon.percentOff}%
              </Text>
            </Box>
            <Box mr={1}>
              <Text color={isLight ? theme.colors.n70 : theme.colors.white}>
                {t("pages:completeSignUpPickSubscription:couponMiddleLabel", {
                  recurringType: t(
                    `pages:completeSignUpPickSubscription:subscriptionProductRecurringLabels:${camelCase(
                      recurringType
                    )}`
                  ).toLowerCase(),
                })}
              </Text>
            </Box>
            <BadgeStyle
              type="alert"
              px={2}
              py={0}
              title={`$${Math.round(discount)}${discountTail}`}
            />
          </Flex>
        )}

        <Flex alignItems="flex-end">
          <H color={isLight ? theme.colors.n90 : theme.colors.white} lg>
            {price ? `$${displayPrice}` : "Free"}
          </H>
          {price ? (
            <Box mb={2}>
              <Text color={isLight ? theme.colors.n70 : theme.colors.n50}>
                /
                {t(
                  `common:subscriptionProductRecurringLabels:${camelCase(
                    RecurringTypesEnum.MONTH
                  )}`
                )}
              </Text>
            </Box>
          ) : (
            <></>
          )}
        </Flex>

        <Flex w="100%" flexDirection="column">
          <div>
            <Badge type="neutral" title={getBadgeTitle()}></Badge>
          </div>
          <ActionButtons
            currentBrokerSubscription={currentBrokerSubscription}
            subscription={subscription}
            cancelButtonLoading={cancelButtonLoading}
            selectButtonLoading={selectButtonLoading}
            recurringType={recurringType}
            onSubscriptionProductCancel={onSubscriptionProductCancel}
            onSubscriptionProductSelect={onSubscriptionProductSelect}
            isLight={isLight}
            type={type}
            onClose={onClose}
          />
        </Flex>

        {/* Features */}
        {featuresList.map((feature, index) => {
          return (
            <Flex mt={3.5} key={index}>
              <Box>
                <Icon
                  color={isLight ? theme.colors.g20 : theme.colors.white}
                  size={14}
                  name="check"
                  mr={2}
                  mt={1.5}
                />
              </Box>
              <Text
                color={isLight ? theme.colors.n90 : theme.colors.white}
                bold={index === 0 && subscription.price > 0}>
                {feature}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
}

export default SubscriptionCard;
